/**
 * Registrar Pagamento — passo 1 do ato de pagar: só a entrada do dinheiro.
 *
 * Os campos são os MESMOS do Step 1 do Registrar Recebimento, e por isso são as mesmas moléculas.
 * O que muda é o que NÃO está aqui: nenhuma seleção de operação ou fatura. Quem diz o que este
 * dinheiro paga é o time de cobrança, depois, no passo 2 — e é justamente essa separação que a
 * tela de Pagamentos existe para sustentar.
 */
import { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import PaymentDateInput, { toIsoInstant } from '../../../molecules/payment/PaymentDateInput';
import PaymentTypeFields, {
    PaymentTypeValue, emptyPaymentTypeValue, assetDationTotal,
} from '../../../molecules/payment/PaymentTypeFields';
import PayerFields, { PayerValue, emptyPayer, onlyDigits } from '../../../molecules/payment/PayerFields';
import { emptyAsset } from '../../../molecules/payment/AssetDationForm';
import { buildPaymentBlock, newPaymentId } from '../../../molecules/payment/paymentPayload';
import { PaymentType, PaymentMethod } from '../../../../constants/payment';
import CurrencyInput from '../../../molecules/CurrencyInput';
import TextEdit from '../../../molecules/TextEdit';
import FileUpload from '../../../molecules/FileUpload';
import { registerPayment, uploadPaymentReceipt } from '../services/billingApi';
import { todayInBrasiliaISO } from '../../../../utils/DateUtils';

const label = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const input = 'w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white';

interface Props {
    onClose: () => void;
    /** chamado após registrar, para a tela recarregar o dia */
    onRegistered: () => void;
    /** data inicial do campo (default: hoje). A tela de Pagamentos passa o dia do filtro,
     *  para o pagamento nascer no mesmo dia que o financeiro está fechando. */
    defaultDate?: string;
}

const RegisterPaymentModal: React.FC<Props> = ({ onClose, onRegistered, defaultDate }) => {
    const [amount, setAmount] = useState(0);
    const [date, setDate] = useState(defaultDate || todayInBrasiliaISO());
    const [time, setTime] = useState('00:00');
    const [showTime, setShowTime] = useState(false);
    const [typeFields, setTypeFields] = useState<PaymentTypeValue>(emptyPaymentTypeValue());
    const [payer, setPayer] = useState<PayerValue>(() => emptyPayer());
    const [notes, setNotes] = useState('');
    const [receiptFile, setReceiptFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);
    /**
     * Gerado ao ABRIR o modal e mantido enquanto ele existir: reenviar este formulário é reenviar
     * o MESMO comando, e o consumidor descarta o segundo. Gerar no submit faria de dois cliques
     * dois pagamentos.
     */
    const [paymentId] = useState(() => newPaymentId());

    /**
     * Muda o Valor Recebido e — em dação — espelha no Valor Estimado do bem.
     *
     * O sentido inverso (bem → valor recebido) já acontece no onChange do PaymentTypeFields; aqui
     * fechamos a via de volta para os dois campos nunca discordarem. Fora da dação, só o valor.
     */
    const handleAmountChange = (next: number) => {
        setAmount(next);
        if (typeFields.payment_type !== PaymentType.ASSET_DATION) return;
        const asset = typeFields.asset_dation ?? emptyAsset();
        if (asset.estimated_value === next) return;
        setTypeFields({ ...typeFields, asset_dation: { ...asset, estimated_value: next } });
    };

    const valor = amount;
    // mesmas condições do Step 1: valor, documento do pagador e — em pagamento financeiro — a
    // conta onde o dinheiro caiu, que é o que o painel reparte
    const podeSalvar = valor > 0
        && onlyDigits(payer.document).length >= 11
        && (typeFields.payment_type !== PaymentType.FINANCIAL || !!typeFields.receiving_bank_account);

    const salvar = async () => {
        setSaving(true);
        try {
            const paymentDate = toIsoInstant(date, time, showTime)!;  // wall-clock local → UTC
            /**
             * O comprovante sobe ANTES do registro: registrar é assíncrono (o pagamento nasce no
             * consumidor do evento), então só o upload prévio consegue mandar o nome do arquivo
             * junto do comando. O nome canônico quem monta é o backend.
             */
            let receiptName = '';
            if (receiptFile) {
                const uploaded = await uploadPaymentReceipt(
                    paymentId, receiptFile, paymentDate, payer.name,
                );
                receiptName = uploaded.fileName;
            }
            await registerPayment(buildPaymentBlock({
                paymentId,
                amount: valor,
                paymentDate,
                typeFields,
                payer,
                notes,
                receiptName,
            }));
            toast.success('Pagamento registrado. Aguardando identificação das faturas.');
            onRegistered();
            onClose();
        } catch (e: any) {
            toast.error(`Não foi possível registrar: ${e.message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                    <div>
                        <h3 className="text-lg font-semibold">Registrar Pagamento</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Entrada do dinheiro. A identificação das faturas é feita depois, pela cobrança.
                        </p>
                    </div>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                </div>

                <div className="px-6 py-4 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <PaymentDateInput label="Data do Pagamento *" date={date} time={time} showTime={showTime}
                                onChange={next => { setDate(next.date); setTime(next.time); setShowTime(next.showTime); }} />
                        </div>
                        <div>
                            {/* `CurrencyInput` e não um input de texto: ele mascara a digitação
                                (dígitos entram pela direita, como caixa de banco) e é o padrão de
                                campo de dinheiro do sistema. Aqui o valor é o que o banco recebeu —
                                digitar 250000 e salvar R$ 250.000,00 achando que eram R$ 2.500,00 é
                                um erro que só aparece na conferência, dias depois. */}
                            <CurrencyInput
                                id="registrar-pagamento-valor"
                                label="Valor Recebido *"
                                prefix="R$"
                                fractionDigits={2}
                                value={amount}
                                onChange={handleAmountChange}
                                className="mb-0"
                            />
                        </div>
                    </div>

                    <div className="mt-4">
                        <PaymentTypeFields
                            value={typeFields}
                            onChange={next => {
                                // dação: o valor recebido acompanha o total dos bens
                                const total = assetDationTotal(next.asset_dation);
                                if (next.payment_type === PaymentType.ASSET_DATION
                                    && total !== assetDationTotal(typeFields.asset_dation)) {
                                    // número direto: o campo deixou de guardar texto quando virou
                                    // CurrencyInput, e formatar aqui devolveria "1.234,56" para um
                                    // estado numérico
                                    setAmount(total);
                                }
                                setTypeFields(next);
                            }}
                            methods={[PaymentMethod.PIX, PaymentMethod.TRANSFER, PaymentMethod.BOLETO]}
                            labelClassName={label}
                            inputClassName={input}
                            idPrefix="registrar-pagamento"
                            // fonte e aval saem daqui: quem os informa é a MARCAÇÃO
                            chargeAttributes={false}
                        />
                    </div>

                    <div className="mt-6 border-t dark:border-slate-700 pt-4">
                        <PayerFields value={payer} onChange={setPayer}
                            labelClassName={label} inputClassName={input} />
                    </div>

                    <div className="mt-6 border-t dark:border-slate-700 pt-4">
                        <label className={label}>Comprovante de pagamento (opcional)</label>
                        <FileUpload
                            id="registrar-pagamento-comprovante"
                            name="comprovante"
                            label="Comprovante"
                            accept="application/pdf"
                            info="Somente PDF"
                            errorMessage=""
                            files={receiptFile ? [receiptFile] : []}
                            onUploadFile={file => setReceiptFile(file)}
                            onRemoveFile={() => setReceiptFile(null)}
                        />
                    </div>

                    <div className="mt-4">
                        <TextEdit
                            id="registrar-pagamento-obs"
                            label="Observações (ex: Referência bancária, NF, etc.)"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder=" "
                        />
                    </div>
                </div>

                <div className="px-6 py-4 border-t dark:border-slate-600 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                        Cancelar
                    </button>
                    <button onClick={salvar} disabled={!podeSalvar || saving}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">
                        {saving ? 'Registrando...' : 'Registrar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RegisterPaymentModal;
